import type { GrovsClient } from '../core/client';
import type { CreateLinkParams } from '../net/api';
import { GrovsError } from '../net/errors';

export class LinkGenerator {
  constructor(private readonly client: GrovsClient) {}

  /**
   * Returns the generated URL, or null on any failure.
   *
   * v1 (grovs_manager.js:151-153) called the error callback when
   * unauthenticated and then issued the request regardless, so a caller could
   * receive an error and a success for one call. Every failure path here
   * returns exactly once.
   */
  async generateLink(params?: CreateLinkParams | null): Promise<string | null> {
    const input = params ?? {};
    if (!this.client.isEnabled) return null;

    if (!this.client.isAuthenticated()) {
      this.client.reportUnavailable(
        'generateLink',
        GrovsError.linkGenerationFailed,
        'The SDK is not authenticated yet; links cannot be generated. ' +
          'Await configure() before calling generateLink().',
      );
      return null;
    }

    // The params are serialized into the request; a bigint or a cycle
    // anywhere in them throws, and the documented contract is null plus
    // onError.
    try {
      // Both: the API serializes `data` on its own, and a non-enumerable
      // property is invisible to a stringify of the whole object.
      JSON.stringify(input);
      if (input.data !== undefined) JSON.stringify(input.data);
    } catch {
      this.client.log.reportError(
        GrovsError.linkGenerationFailed,
        'The link parameters could not be serialized to JSON.',
      );
      return null;
    }

    const generation = this.client.lifecycleGeneration;
    const response = await this.client.service.createLink(input);
    // A reset since the request left means this link is the previous
    // visitor's; messages and the payload lookup check the same way. Reported
    // like every other failure here — the contract is null plus onError.
    if (generation !== this.client.lifecycleGeneration) {
      this.client.log.reportError(
        GrovsError.linkGenerationFailed,
        'The SDK was reset while the link was being generated.',
      );
      return null;
    }

    if (!response.ok) {
      this.client.log.reportError(
        GrovsError.linkGenerationFailed,
        `Link generation failed with status ${response.status}.`,
      );
      return null;
    }

    const link = (response.body as Record<string, unknown> | null)?.['link'];
    if (typeof link !== 'string' || !link) {
      this.client.log.reportError(
        GrovsError.linkGenerationFailed,
        'The backend returned no link. Configure the redirect rules for this project ' +
          'in the Grovs console first.',
      );
      return null;
    }

    return link;
  }

  /**
   * Details for a link previously generated by the SDK in this environment.
   * Returns null when the path is unknown — the backend answers `null` with a
   * 200 rather than a 404, so an absent link is not an error.
   *
   * The body is passed through untyped: it carries whatever the backend
   * declares for a link, including the tri-state `copy_to_clipboard_ios` /
   * `copy_to_clipboard_android` (`true` / `false` / `null` for inherit; see
   * the backend design note cited in net/api.ts).
   */
  async linkDetails(path: string): Promise<Record<string, unknown> | null> {
    if (!this.client.isEnabled) return null;

    if (!this.client.isAuthenticated()) {
      this.client.reportUnavailable(
        'linkDetails',
        GrovsError.networkRequestFailed,
        'The SDK is not authenticated yet; link details cannot be fetched.',
      );
      return null;
    }

    const generation = this.client.lifecycleGeneration;
    const response = await this.client.service.linkDetails(path);
    if (generation !== this.client.lifecycleGeneration) {
      this.client.log.reportError(
        GrovsError.networkRequestFailed,
        'The SDK was reset while the link details were being fetched.',
      );
      return null;
    }
    if (!response.ok) {
      this.client.log.reportError(
        GrovsError.networkRequestFailed,
        `Link details failed with status ${response.status}.`,
      );
      return null;
    }

    return response.body && typeof response.body === 'object'
      ? (response.body as Record<string, unknown>)
      : null;
  }
}
